import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { useTheme } from "@mui/material/styles";
import AddEditButton from "../../reusable-components/AddEditButton/AddEditButton";
import { Alert, Dialog, DialogContent, Grid, Typography } from "@mui/material";
import DialogTitle from "../DialogTitle/DialogTitle";
import AutocompleteDropdown from "../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { CompanyLegalEntitiesApiService } from "../../services/company-legal-entities";
import AddNewDocument from "../AddNewDocument/AddNewDocument";
import { DOCUMENT_CATEGORIES, DOCUMENT_TYPES } from "../../utils/constants/constants";
import { DocumentCategoryRef } from "../../models/document-categories";
import { DocumentTypeRef } from "../../models/document-types";
import { SplitEmployeeDocumentDTO } from "../../models/split-document/split-employee-document/split-employee-document-dto";
import { SplitEmployeeDocumentSTO } from "../../models/split-document/split-employee-document/split-employee-document-sto";
import { SplitEmployeeDocumentApiService } from "../../services/split-document/split-employee-document/split-employee-document-api.service";
import SplitEmployeeDocumentFiles from "./SplitEmployeeDocumentFiles/SplitEmployeeDocumentFiles";
import { CompanyLegalEntityDTO } from "../../models/company-accounts/company-legal-entities";
import { useAppSelector } from "../../custom-hooks/redux/redux";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

interface Props {
  handleNotifyParent: () => void;
  legalEntity: CompanyLegalEntityDTO | null;
}

interface State {
  open: boolean;
  openFileList: boolean;
  item: SplitEmployeeDocumentSTO;
  loading: boolean;
  error: string;
}

const SplitEmployeeDocumentAction = ({ handleNotifyParent, legalEntity }: Props) => {
  const theme = useTheme();
  const currCompanyAccount = useAppSelector((state) => state.currentUserReducer.currentCompanyAccount);
  const DEFAULT_ITEM: SplitEmployeeDocumentSTO = useMemo(
    () => ({
      id: "",
      isDeleted: false,
      companyAccount: legalEntity?.companyAccount ?? null,
      legalEntity: legalEntity,
      originalDocument: null,
    }),
    [legalEntity]
  );

  const [state, setState] = useState<State>({
    open: false,
    openFileList: false,
    item: DEFAULT_ITEM,
    loading: false,
    error: "",
  });

  const handleChangeField = useCallback(
    (field: keyof SplitEmployeeDocumentSTO, value: SplitEmployeeDocumentSTO[keyof SplitEmployeeDocumentSTO]) => {
      setState((state) => ({
        ...state,
        item: {
          ...state.item,
          [field]: value,
          ...(field === "legalEntity" && {
            companyAccount: (value as CompanyLegalEntityDTO)?.companyAccount ?? null,
          }),
        },
      }));
    },
    []
  );

  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    console.log(state.item);
    // first we save the item
    // second we automatically trigger the split process

    const splitDocId = uuid();
    try {
      const response = await new SplitEmployeeDocumentApiService().saveItems([
        {
          ...state.item,
          id: splitDocId,
        },
      ]);
      // setState((state) => ({
      //   ...state,
      //   open: false,
      //   openFileList: true,
      // }));
      if (response.error) {
        setState((state) => ({ ...state, error: response.error }));
      } else {
        handleNotifyParent && handleNotifyParent();

        setState((state) => ({
          ...state,
          item: {
            ...state.item,
            id: splitDocId,
          },
          open: false,
          openFileList: true,
        }));
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [state.item, handleNotifyParent]);

  useEffect(() => {
    if (state.item.originalDocument) {
      handleSave();
    }
  }, [
    state.item.originalDocument,
    // handleSave
  ]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      item: {
        ...state.item,
        legalEntity: legalEntity,
        companyAccount: legalEntity?.companyAccount ?? null,
      },
    }));
  }, [legalEntity]);

  // useEffect(() => {
  //   setState((state) => ({
  //     ...state,
  //     item: {
  //       ...state.item,
  //       companyAccount : state.item.legalEntity?.companyAccount ?? null
  //     },
  //   }));
  // }, [state.item.legalEntity]);

  return (
    <>
      <AddEditButton
        title="split"
        effect="add"
        onClick={() => setState((state) => ({ ...state, open: true }))}
        buttonText="Split new document"
        customStyle={{
          backgroundColor: theme.palette.primary.main,
          borderRadius: "5px",
          fontSize: "0.7rem",
          color: theme.palette.primary.contrastText,
          padding: "5px",
        }}
      />
      <Dialog open={state.open}>
        <DialogTitle
          title="Split document files"
          handleClose={() => setState((state) => ({ ...state, open: false }))}
        />
        <DialogContent>
          <LoadingOverlay
            loading={state.loading}
            error={state.error}
          >
            <Grid
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
              }}
            >
              <Grid style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                <Typography>Legal entity</Typography>
                <AutocompleteDropdown
                  selectedValue={state.item.legalEntity}
                  serviceApi={new CompanyLegalEntitiesApiService()}
                  field={"legalEntity"}
                  handleChange={handleChangeField}
                  inputStyle={{
                    width: "300px",
                  }}
                  disabled={!!legalEntity}
                />
              </Grid>
              {state.item.legalEntity ? (
                <AddNewDocument
                  buttonType="text"
                  handleNotifyParent={(document) => {
                    handleChangeField("originalDocument", document);
                  }}
                  displayExpandableItemUponSave={false}
                  documentFieldsProps={{
                    category: DOCUMENT_CATEGORIES.OTHER as DocumentCategoryRef,
                    type: DOCUMENT_TYPES.DocumentToSplit as DocumentTypeRef,
                  }}
                  readOnlyFields={{
                    isEmployeeRO: true,
                    isCategoryRO: true,
                    isLegalEntityRO: true,
                    isTypeRO: true,
                    isDateRO: true,
                    isStatusRO: true,
                  }}
                  hideReadOnlyFields={true}
                  maxFilesNumber={1}
                  wrappedInDialog={false}
                  customSaveTitle={"Split document"}
                />
              ) : (
                <Alert severity="info">Please select a legal entity to allow uploading a file</Alert>
              )}
            </Grid>
          </LoadingOverlay>
        </DialogContent>
      </Dialog>
      {!state.loading && state.openFileList ? (
        <SplitEmployeeDocumentFiles
          open={state.openFileList}
          triggeredByParent
          item={state.item as SplitEmployeeDocumentDTO}
        />
      ) : (
        <></>
      )}
    </>
  );
};

export default SplitEmployeeDocumentAction;
